import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {ActionButton} from 'screens/commons';

import IMAGES from 'constants/Images';

const ChoiceComponent = ({apiResponse, handleSubmit}) => {
  const [selectedOption, setSelectedOption] = useState('');
  const [disableButton, setDisableButton] = useState(true);

  const handleOptionChange = (value) => {
    setSelectedOption(value);
  };

  useEffect(() => {
    if (selectedOption) {
      setDisableButton(false);
    } else {
      setDisableButton(true);
    }
  }, [selectedOption]);

  const options = apiResponse?.schema?.fields?.[0]?.meta?.options || [];

  // Map options to fit our enhanced UI with additional metadata
  const enhancedOptions = options.map((option) => {
    // Default enhancement based on option value - can be customized based on actual data
    const isDigilocker =
      option.value.toLowerCase().includes('digi') || option.label.toLowerCase().includes('digi');

    return {
      ...option,
      isRecommended: isDigilocker,
      icon: isDigilocker ? (
        <img src={IMAGES.digiLocker} alt="DigiLocker" style={{height: '4rem'}} />
      ) : (
        <img src={IMAGES.uploadPanIcon} alt="Upload" style={{height: '4rem'}} />
      ),
      iconVariant: isDigilocker ? 'primary' : 'default',
      features: isDigilocker
        ? [
            {
              icon: (
                <img src={IMAGES.questionMark} alt="Aadhaar OTP" style={{width: 20, height: 20}} />
              ),
              text: 'You need to verify this step with Aadhaar OTP.',
            },
            {
              icon: <img src={IMAGES.clockIcon} alt="Clock" style={{width: 20, height: 20}} />,
              text: 'It take up to 1 to 2 days to approve your profile.',
            },
          ]
        : [
            {
              icon: <img src={IMAGES.fileIcon} alt="File" style={{width: 20, height: 20}} />,
              text: 'You need to have your Aadhaar and PAN images ready to upload.',
            },
            {
              icon: <img src={IMAGES.clockIcon} alt="Clock" style={{width: 20, height: 20}} />,
              text: 'It may take up to 7 working days to approve your profile.',
            },
          ],
      infoMessage: isDigilocker
        ? 'Using DigiLocker ensures faster processing as documents are pre-verified by the government'
        : 'Ensure your documents are clearly visible and all information is legible to avoid delays',
      infoVariant: isDigilocker ? 'info' : 'warning',
    };
  });

  return (
    <Container>
      <Title
        className="section-heading"
        dangerouslySetInnerHTML={{__html: apiResponse?.pageTitle}}
      />

      <CardContent>
        <OptionsList>
          {enhancedOptions.map((option) => (
            <OptionCard
              key={option.value}
              selected={selectedOption === option.value}
              onClick={() => handleOptionChange(option.value)}
            >
              <OptionContent>
                <RadioCircle className="flex-shrink-0" checked={selectedOption === option.value} />
                <OptionDetails>
                  <OptionHeader>
                    <OptionLabel className="body-medium">{option.label}</OptionLabel>
                    {option.isRecommended && (
                      <Badge className="body-small font-bold">Recommended</Badge>
                    )}
                  </OptionHeader>
                  <OptionFeatures>
                    <div>
                      <FeaturesList>
                        {option.features.map((feature, index) => (
                          <FeatureItem key={index}>
                            {feature.icon}
                            <span className="body-small">{feature.text}</span>
                          </FeatureItem>
                        ))}
                      </FeaturesList>
                    </div>
                  </OptionFeatures>

                  <InfoBox variant={option.infoVariant}>
                    <img src={IMAGES.infoIcon} alt="Info" style={{width: 16, height: 16}} />
                    <p className="body-small">{option.infoMessage}</p>
                  </InfoBox>
                </OptionDetails>
                {option.icon}
              </OptionContent>
            </OptionCard>
          ))}
        </OptionsList>

        <StyledActionButton
          buttonText="Continue"
          callback={disableButton ? null : () => handleSubmit(selectedOption)}
          isLoading={false}
          isDisabled={disableButton}
          rightIcon={<img src={IMAGES.arrowRight} alt="Continue" style={{width: 20, height: 20}} />}
        />
      </CardContent>
    </Container>
  );
};

export default ChoiceComponent;

// Icons (as variables to be easily replaced with actual icons)
const Container = styled.div`
  margin: 0 auto;
`;

const Title = styled.h1`
  color: ${(props) => props.theme.colors?.text?.primary || '#000000'};
  margin-top: 1.5rem;
  margin-left: 1.5rem;
  margin-bottom: 0;
  padding: 0;
`;

const CardContent = styled.div`
  padding: 2rem;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const OptionCard = styled.div`
  position: relative;
  padding: 1rem;
  border-radius: ${(props) => props.theme.borderRadius?.md || '0.5rem'};
  border: 2px solid
    ${(props) =>
      props.selected
        ? props.theme.colors?.brand?.primary || '#9333ea'
        : props.theme.colors?.border?.default || '#e2e8f0'};
  background-color: ${(props) =>
    props.selected ? props.theme.colors?.brand?.secondary || '#f5f3ff' : 'transparent'};
  transition: all 0.2s ease-in-out;
  cursor: pointer;

  &:hover {
    border-color: ${(props) =>
      props.selected
        ? props.theme.colors?.brand?.primary || '#9333ea'
        : props.theme.colors?.brand?.tertiary || '#cbd5e1'};
  }
`;

const OptionContent = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const RadioCircle = styled.div`
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 50%;
  border: 2px solid
    ${(props) =>
      props.checked
        ? props.theme.colors?.brand?.primary || '#9333ea'
        : props.theme.colors?.text?.secondary || '#cbd5e1'};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.125rem;

  &::after {
    content: '';
    width: 0.625rem;
    height: 0.625rem;
    border-radius: 50%;
    background-color: ${(props) =>
      props.checked ? props.theme.colors?.brand?.primary || '#9333ea' : 'transparent'};
  }
`;

const OptionDetails = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const OptionLabel = styled.label`
  color: ${(props) => props.theme.colors?.text?.primary || '#000000'};
  cursor: pointer;
`;

const Badge = styled.span`
  background-color: ${(props) => props.theme.colors?.feedback?.successLight || '#dcfce7'};
  color: ${(props) => props.theme.colors?.feedback?.success || '#166534'};
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
`;

const OptionFeatures = styled.div`
  display: flex;
`;

const FeaturesList = styled.ul`
  flex: 1;
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const FeatureItem = styled.li`
  display: flex;
  align-items: flex-start;

  & > *:first-child {
    margin-right: 0.5rem;
    margin-top: 0.125rem;
    color: ${(props) => props.theme.colors?.text?.secondary || '#64748b'};
    flex-shrink: 0;
  }

  & > span {
    ${(props) =>
      props.theme.typography?.styles?.body?.small ||
      `
      font-size: 0.875rem;
    `}
    color: ${(props) => props.theme.colors?.text?.primary || '#000000'};
  }
`;

const InfoBox = styled.div`
  background-color: ${(props) =>
    props.variant === 'warning'
      ? props.theme.colors?.feedback?.warning + '20' || '#fef3c7'
      : props.theme.colors?.brand?.tertiary + '20' || '#dbeafe'};
  padding: 0.75rem;
  border-radius: 8px;
  display: flex;
  align-items: flex-start;

  & > *:first-child {
    margin-right: 0.5rem;
    margin-top: 0.125rem;
    color: ${(props) =>
      props.variant === 'warning'
        ? props.theme.colors?.feedback?.warning || '#d97706'
        : props.theme.colors?.brand?.primary || '#2563eb'};
    flex-shrink: 0;
  }

  & > p {
    color: ${(props) =>
      props.variant === 'warning'
        ? props.theme.colors?.text?.primary || '#92400e'
        : props.theme.colors?.text?.primary || '#1e40af'};
    margin: 0;
  }
`;

const StyledActionButton = styled(ActionButton)`
  width: 100%;
  max-width: 640px;
  margin: 3rem auto;
`;
